import { inject, Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { RecipientsInterface } from '../../interfaces/recipients.interface';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { RecipientModel } from '../../models/low-level/recipient.model';
import { LoadStatus } from '../../state/load-status';
import {
    initRecipients,
    removeRecipientsRequested,
    upsertRecipientsRequested,
} from '../../state/recipients/actions/recipients.actions';
import { recipientsFromState, recipientsToState } from '../../state/recipients/recipients.converters';
import { dispatchRecipientsWrite } from '../../state/recipients/recipients-write-bridge';
import {
    selectRecipientsForNotificationId,
    selectRecipientsStatus,
} from '../../state/recipients/selectors/recipients.selector';
import { NotificationStateUtilities } from '../../state';

/**
 * NgRx-backed implementation of {@link RecipientsInterface}. The saved-per-notification
 * recipient collections live in the recipients store slice, resolved by
 * `RecipientsEffects`; reads go through selectors, writes go through
 * `dispatchRecipientsWrite`.
 *
 * Two deliberate fidelity deltas from the legacy `LocalStorageRecipientsService`:
 *
 * 1. Legacy `init()` pushes onto its in-memory array without clearing it first, so a
 *    second `init()` call would duplicate every entry. `setEntriesReducer` always
 *    replaces the collection instead — a fix, not a regression, since `init()` is only
 *    ever called once during app startup.
 * 2. Legacy stores the caller's live `RecipientModel[]` reference, so later in-place
 *    mutations of that array leak into storage without a re-save. The slice snapshots
 *    plain data at save time instead. Every call site re-passes the current array on each
 *    save, so nothing depends on the legacy leak.
 */
@Injectable({
    providedIn: 'root',
})
export class RecipientsNgrxService implements RecipientsInterface {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);

    /** Monotonic counter for write correlation ids (no caller awaits a concurrent write). */
    private writeCounter = 0;

    public init(): Observable<void> {
        this.store.dispatch(initRecipients());
        return this.store.select(selectRecipientsStatus).pipe(
            filter((status) => status === LoadStatus.Loaded),
            take(1),
            map(() => undefined as void),
        );
    }

    public getRecipientsByNotificationId(id: NotificationIdentifierModel): RecipientModel[] | undefined {
        const entries = this.store.selectSignal(
            selectRecipientsForNotificationId(NotificationStateUtilities.toState(id)),
        )();
        if (entries) {
            return recipientsFromState(entries);
        }
        return undefined;
    }

    public saveRecipientsByNotificationId(
        recipients: RecipientModel[],
        id: NotificationIdentifierModel,
    ): Observable<unknown> {
        const correlationId = `recipients-save-${++this.writeCounter}`;
        return dispatchRecipientsWrite(
            this.store,
            this.actions$,
            upsertRecipientsRequested({
                notificationId: NotificationStateUtilities.toState(id),
                recipients: recipientsToState(recipients),
                correlationId,
            }),
            correlationId,
        );
    }

    public removeRecipientsFromLocalStorage(id: NotificationIdentifierModel): Observable<unknown> {
        const correlationId = `recipients-remove-${++this.writeCounter}`;
        return dispatchRecipientsWrite(
            this.store,
            this.actions$,
            removeRecipientsRequested({ notificationId: NotificationStateUtilities.toState(id), correlationId }),
            correlationId,
        );
    }
}
